import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ClockClockwise, FunnelSimple, CaretLeft, CaretRight, User, Briefcase, Gear } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_COLORS = {
  placement_created: 'bg-blue-100 text-blue-700',
  placement_approved: 'bg-emerald-100 text-emerald-700',
  placement_rejected: 'bg-red-100 text-red-700',
  user_created: 'bg-emerald-100 text-emerald-700',
  user_updated: 'bg-amber-100 text-amber-700',
  user_deactivated: 'bg-orange-100 text-orange-700',
  user_restored: 'bg-teal-100 text-teal-700',
  user_deleted: 'bg-red-100 text-red-700',
  user_password_reset: 'bg-purple-100 text-purple-700',
  team_created: 'bg-blue-100 text-blue-700',
  team_updated: 'bg-amber-100 text-amber-700',
  team_members_updated: 'bg-cyan-100 text-cyan-700',
  team_deleted: 'bg-red-100 text-red-700',
  custom_field_created: 'bg-purple-100 text-purple-700',
  custom_field_deleted: 'bg-red-100 text-red-700',
  custom_field_values_updated: 'bg-cyan-100 text-cyan-700',
};

const ACTION_ICONS = {
  placement_created: <Briefcase size={14} />,
  placement_approved: <Briefcase size={14} />,
  user_updated: <User size={14} />,
  custom_field_created: <Gear size={14} />,
};

const AuditLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/admin/audit-logs?page=${page}&per_page=30`;
      if (actionFilter) url += `&action=${actionFilter}`;
      if (entityFilter) url += `&entity_type=${entityFilter}`;
      const res = await axios.get(url, { withCredentials: true });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, actionFilter, entityFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / 30);

  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="audit-log-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Audit Log</h1>
        <p className="text-sm text-slate-500 mt-0.5">Comprehensive record of all changes across the portal</p>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <FunnelSimple size={16} className="text-slate-400" />
          <Input value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setPage(1); }} placeholder="Filter by action..." className="h-8 w-48 text-xs" data-testid="action-filter" />
          <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v === '_all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="entity-filter"><SelectValue placeholder="All entities" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All entities</SelectItem>
              <SelectItem value="placement">Placements</SelectItem>
              <SelectItem value="user">Users</SelectItem>
              <SelectItem value="custom_field">Custom Fields</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-400 ml-auto">{total} total entries</span>
        </CardContent>
      </Card>

      {/* Log Entries */}
      {loading ? (
        <div className="text-center py-10 text-slate-400">Loading...</div>
      ) : logs.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="text-center py-12 text-slate-400">
          <ClockClockwise size={32} className="mx-auto mb-3 text-slate-300" />
          <p>No audit log entries found</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {logs.map((log, idx) => (
            <Card key={idx} className="border-slate-100" data-testid={`log-entry-${idx}`}>
              <CardContent className="p-3 flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-600'}`}>
                  {ACTION_ICONS[log.action] || <ClockClockwise size={14} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={(ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-600') + ' text-[10px]'}>{log.action?.replace(/_/g, ' ').toUpperCase()}</Badge>
                    <Badge variant="outline" className="text-[10px]">{log.entity_type}</Badge>
                    <span className="text-[10px] text-slate-400 font-mono">{log.entity_id?.slice(0, 12)}...</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    by <span className="font-semibold">{log.performed_by_name}</span>
                  </p>
                  {log.details && Object.keys(log.details).length > 0 && (
                    <div className="mt-1.5 text-[11px] text-slate-500 bg-slate-50 rounded p-2">
                      {Object.entries(log.details).map(([k, v]) => (
                        <div key={k} className="flex gap-2">
                          <span className="text-slate-400">{k.replace(/_/g, ' ')}:</span>
                          <span className="text-slate-700 font-medium">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <span className="text-[10px] text-slate-400 whitespace-nowrap flex-shrink-0">
                  {new Date(log.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="prev-page"><CaretLeft size={14} /></Button>
          <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="next-page"><CaretRight size={14} /></Button>
        </div>
      )}
    </div>
  );
};

export default AuditLogPage;
